import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fleetKeys } from '../hooks';
import { miloKeys } from '../milo/hooks';
import {
  deleteSimTruck,
  getSimulation,
  restartSimulation,
  setSimulationAi,
  startSimulation,
  stepSimulation,
  stopSimulation,
  type SimOverview,
  type SimStatus,
} from './api';

const simulationKey = [...fleetKeys.all, 'simulation'] as const;

/** null: the simulation is off. Asked again every 5 s only while a session exists */
export const useSimulation = () =>
  useQuery({
    queryKey: simulationKey,
    queryFn: getSimulation,
    retry: false,
    refetchInterval: (query) => (query.state.data?.status ? 5000 : false),
  });

/** Every action puts the new status in the cache; the SIM data it changed refreshes everywhere else */
export function useSimulationActions() {
  const queryClient = useQueryClient();
  const settle = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: fleetKeys.all }),
      queryClient.invalidateQueries({ queryKey: miloKeys.all }),
    ]);
  const setStatus = (status: SimStatus | null) =>
    queryClient.setQueryData<SimOverview | null>(simulationKey, (old) => (old ? { ...old, status } : old));

  const withStatus = <T>(fn: (input: T) => Promise<SimStatus>) => ({
    mutationFn: fn,
    onSuccess: setStatus,
    onSettled: settle,
  });

  return {
    start: useMutation(withStatus(startSimulation)),
    step: useMutation(withStatus(stepSimulation)),
    restart: useMutation(withStatus(restartSimulation)),
    setAi: useMutation(withStatus(setSimulationAi)),
    stop: useMutation({ mutationFn: stopSimulation, onSuccess: () => setStatus(null), onSettled: settle }),
    remove: useMutation({
      mutationFn: deleteSimTruck,
      onSuccess: (_, id) =>
        queryClient.setQueryData<SimOverview | null>(simulationKey, (old) =>
          old ? { ...old, simTrucks: old.simTrucks.filter((truck) => truck.id !== id) } : old
        ),
      onSettled: settle,
    }),
  };
}
